import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

const CsAndItTable16 = ({ data }) => {
  const pdfRef = useRef();

  if (!data?.length) return <p>No data available</p>;

  const years = data.map((item) => item.year);
  const femaleEmployment = data.map((item) => Number(item.male)); // name mismatch, keeping structure

  /** --- Trend Line (Moving Average) --- **/
  const trendline = femaleEmployment.map((value, index, arr) =>
    index === 0 ? value : Math.round((arr[index - 1] + value) / 2),
  );

  /** --- Summary Calculations --- **/
  const first = femaleEmployment[0];
  const last = femaleEmployment[femaleEmployment.length - 1];
  const growthPercent = (((last - first) / first) * 100).toFixed(1);

  const peak = data.reduce(
    (high, row) =>
      Number(row.male) > high.val
        ? { year: row.year, val: Number(row.male) }
        : high,
    { year: years[0], val: femaleEmployment[0] },
  );

  const avgValue = Math.round(
    femaleEmployment.reduce((sum, n) => sum + n, 0) / femaleEmployment.length,
  );

  /** --- Chart Data --- **/
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Female Employment (High School)",
        data: femaleEmployment,
        backgroundColor: "#EC489970", // indigo
        borderColor: "#EC4899",
        borderWidth: 2,
      },
      {
        label: "Trend Line",
        data: trendline,
        borderColor: "#EF4444",
        backgroundColor: "#EF4444",
        type: "line",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
      },
    ],
  };

  /** --- Chart Options --- **/
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "High School Graduate Female Workforce Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  /** --- PDF Export --- **/
 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text(APP_VERSION, 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("HighSchool_Female_Employment_Trends.pdf");
   });
 };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white shadow-lg rounded-xl border border-gray-200"
      >
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          High School Graduate Female Workforce (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-blue-100 rounded-lg text-center shadow">
            <p className="text-gray-600">Growth Rate</p>
            <p className="text-xl font-bold text-blue-800">+{growthPercent}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-lg text-center shadow">
            <p className="text-gray-600">Peak Year</p>
            <p className="text-xl font-bold text-yellow-800">{peak.year}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-lg text-center shadow">
            <p className="text-gray-600">Avg Employment</p>
            <p className="text-xl font-bold text-green-800">
              {avgValue.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female Employment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td
                        className="p-2 text-indigo-700 font-semibold"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.male).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-5">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-5 py-2 rounded-lg flex items-center gap-2"
        >
          Download as PDF
          <img src={pdfIcon} className="w-6" alt="PDF" />
        </button>
      </div>
    </>
  );
};

export default CsAndItTable16;

